import React, { useRef, useState } from "react";
import { Camera, Upload, X } from "lucide-react";
import { isNative, pickPhoto } from "../lib/native";
import { toast } from "sonner";

export const UploadZone = ({ onImage, preview, onClear }) => {
  const inputRef = useRef(null);
  const cameraRef = useRef(null);
  const [drag, setDrag] = useState(false);

  const handleFile = (f) => {
    if (!f) return;
    if (!f.type.startsWith("image/")) return;
    const reader = new FileReader();
    reader.onload = (e) => onImage(e.target.result);
    reader.readAsDataURL(f);
  };

  // Native (Capacitor) camera/library picker — used inside the Android/iOS
  // shell. On web this returns null and we fall back to the existing <input>
  // flows to keep parity.
  const handleNativePick = async (source) => {
    try {
      const dataUrl = await pickPhoto({ source });
      if (dataUrl) onImage(dataUrl);
    } catch (e) {
      // User cancelled the picker — silent. Anything else: toast for visibility.
      if (e?.message && !/cancel/i.test(e.message)) toast.error(e.message);
    }
  };

  const onCameraClick = () => {
    if (isNative()) return handleNativePick("camera");
    cameraRef.current?.click();
  };
  const onUploadClick = () => {
    if (isNative()) return handleNativePick("library");
    inputRef.current?.click();
  };

  if (preview) {
    return (
      <div className="relative rounded-3xl overflow-hidden border border-[#E5E0D8] bg-white">
        <img src={preview} alt="Crop preview" className="w-full h-72 object-cover" />
        <button
          data-testid="clear-image-btn"
          onClick={onClear}
          className="absolute top-3 right-3 bg-white/90 hover:bg-white rounded-full p-2 shadow-md transition-colors"
          aria-label="Remove image"
        >
          <X className="w-5 h-5 text-[#1C1C1C]" />
        </button>
      </div>
    );
  }

  return (
    <div
      data-testid="upload-zone"
      onDragOver={(e) => {
        e.preventDefault();
        setDrag(true);
      }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDrag(false);
        handleFile(e.dataTransfer.files?.[0]);
      }}
      className={`cd-upload-zone ${drag ? "cd-drag" : ""} rounded-3xl p-6 sm:p-10 flex flex-col items-center justify-center gap-4 text-center`}
    >
      <div className="w-16 h-16 rounded-2xl bg-[#1B4332] flex items-center justify-center">
        <Camera className="w-8 h-8 text-[#D4A373]" strokeWidth={1.8} />
      </div>
      <div>
        <h3 className="cd-heading text-xl sm:text-2xl font-bold text-[#1C1C1C]">
          Scan your crop
        </h3>
        <p className="text-sm text-[#52796F] mt-1">
          Take a photo or upload a clear image of the affected leaf or plant
        </p>
      </div>
      <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
        <button
          data-testid="camera-btn"
          onClick={onCameraClick}
          className="bg-[#1B4332] text-white hover:bg-[#2D6A4F] px-6 py-3 rounded-full font-semibold flex items-center justify-center gap-2 cd-lift"
        >
          <Camera className="w-5 h-5" />
          Use Camera
        </button>
        <button
          data-testid="upload-btn"
          onClick={onUploadClick}
          className="bg-white text-[#1B4332] border-2 border-[#1B4332] hover:bg-[#F4F1EA] px-6 py-3 rounded-full font-semibold flex items-center justify-center gap-2 cd-lift"
        >
          <Upload className="w-5 h-5" />
          Upload Photo
        </button>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        data-testid="file-input"
        onChange={(e) => handleFile(e.target.files?.[0])}
      />
      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        data-testid="camera-input"
        onChange={(e) => handleFile(e.target.files?.[0])}
      />
    </div>
  );
};
